/**
 * "Switch from Timetastic" — the importer UI (Settings → Import).
 *
 * Flow (dry-run ethos, like everything else in this project):
 *   1. Upload the FULL BACKUP .xlsx from Timetastic's Reporting & backup page.
 *   2. PREVIEW: what we found, how each leave type will map (editable), every
 *      column we recognised/ignored, and any warnings. Nothing written yet.
 *   3. Import (idempotent — re-running updates, never duplicates), with an
 *      optional "send login invites" step afterwards (default OFF: nobody
 *      gets a surprise email before the admin has checked the data landed).
 */
import React, { useState } from 'react';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Loader2, Users, CalendarDays, Building2 } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { importApi, usersApi } from '../lib/api';
import { toast } from 'sonner';

const Chip = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
    <Icon className="w-4 h-4 text-primary" />
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-semibold">{value}</span>
  </div>
);

export default function TimetasticImport() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [typeMap, setTypeMap] = useState({});      // tt name -> select value
  const [sendInvites, setSendInvites] = useState(false);
  const [selfEmail, setSelfEmail] = useState('');   // "that's me" (admin's file identity)
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [inviteProgress, setInviteProgress] = useState(null);

  const reset = () => {
    setFile(null); setPreview(null); setTypeMap({});
    setResult(null); setInviteProgress(null); setSendInvites(false);
    setSelfEmail('');
  };

  const doPreview = async () => {
    if (!file) { toast.error('Choose the Timetastic full-backup Excel file first'); return; }
    setBusy(true);
    try {
      const r = await importApi.previewTimetastic(file);
      setPreview(r.data);
      // Seed the mapping selects from the server's suggestions.
      const seed = {};
      Object.entries(r.data.leave_types || {}).forEach(([name, s]) => {
        seed[name] = s.suggested_leave_type_id
          ? `id:${s.suggested_leave_type_id}`
          : (s.create_deducted ? 'create:deducted' : 'create:record');
      });
      setTypeMap(seed);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't read that file");
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const options = { type_map: {} };
      Object.entries(typeMap).forEach(([name, v]) => {
        if (v.startsWith('id:')) options.type_map[name] = { leave_type_id: v.slice(3) };
        else options.type_map[name] = { create: true, deducted: v === 'create:deducted' };
      });
      if (selfEmail) options.self_email = selfEmail;
      const r = await importApi.applyTimetastic(file, options);
      setResult(r.data);
      toast.success('Import complete');

      if (sendInvites) {
        const active = (r.data.imported_users || []).filter(u => !u.archived);
        // Email-less people (Timetastic-style) can't be invited — that's by
        // design, not a failure. Skip them and say so, rather than counting
        // them as "failed".
        const targets = active.filter(u => u.email);
        const skipped = active.length - targets.length;
        let sent = 0, failed = 0;
        for (const u of targets) {
          setInviteProgress({ sent, failed, total: targets.length, skipped });
          try { await usersApi.sendInvite(u.id); sent += 1; }
          catch (e) { failed += 1; }
        }
        setInviteProgress({ sent, failed, total: targets.length, skipped, done: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed — nothing may have been written; re-running is safe');
    } finally {
      setBusy(false);
    }
  };

  // ----- step 3: results -------------------------------------------------
  if (result) {
    return (
      <div className="card-hive p-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-4">
          <CheckCircle2 className="w-6 h-6 text-green-500" />
          <h2 className="font-heading text-xl font-semibold">Import complete</h2>
        </div>
        <ul className="text-sm space-y-1 mb-4">
          <li>{result.users_created || 0} employees created, {result.users_updated || 0} updated</li>
          <li>{result.departments_created || 0} departments created</li>
          <li>{result.leave_types_created || 0} leave types created</li>
          <li>{result.bookings_created || 0} bookings imported{result.bookings_skipped_existing ? ` (${result.bookings_skipped_existing} already existed — skipped)` : ''}</li>
          <li>{result.allowance_rows_created || 0} allowance rows created across the years{result.allowance_rows_merged ? `, ${result.allowance_rows_merged} merged with existing accounts` : ''}</li>
          {result.self_mapped ? <li>Your file identity was merged onto your own account — no duplicate you</li> : null}
        </ul>
        {inviteProgress && (
          <p className="text-sm mb-4">
            Invites: {inviteProgress.sent} sent{inviteProgress.failed ? `, ${inviteProgress.failed} failed` : ''} of {inviteProgress.total}
            {inviteProgress.skipped ? ` · ${inviteProgress.skipped} skipped (no email address)` : ''}
            {!inviteProgress.done && <Loader2 className="inline w-3.5 h-3.5 ml-2 animate-spin" />}
          </p>
        )}
        {(result.warnings || []).length > 0 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 mb-4">
            {result.warnings.map((w, i) => (
              <p key={i} className="text-xs text-muted-foreground">{w}</p>
            ))}
          </div>
        )}
        <p className="text-sm text-muted-foreground mb-4">
          Spot-check a few employees (balances, booking history, working days),
          then invite anyone who wasn't invited. Re-running the import with the
          same file is safe — it updates rather than duplicates.
        </p>
        <Button variant="outline" onClick={reset}>Import another file</Button>
      </div>
    );
  }

  // ----- step 2: preview ---------------------------------------------------
  if (preview) {
    const year = new Date().getFullYear();
    return (
      <div className="space-y-6 max-w-3xl">
        <div className="card-hive p-6">
          <h2 className="font-heading text-xl font-semibold mb-1">
            Ready to import{preview.organisation?.name ? ` — ${preview.organisation.name}` : ''}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Nothing has been written yet. Check the plan below, then import.
          </p>
          <div className="flex flex-wrap gap-3 mb-5">
            <Chip icon={Users} label="Employees" value={preview.counts.users} />
            <Chip icon={Building2} label="Departments" value={preview.counts.departments} />
            <Chip icon={CalendarDays} label="Bookings" value={preview.counts.bookings} />
          </div>

          {/* Employees */}
          <div className="border border-border rounded-lg overflow-hidden mb-5">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Employee</th>
                  <th className="px-3 py-2 font-medium">Allowance {year}</th>
                  <th className="px-3 py-2 font-medium">Works</th>
                  <th className="px-3 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {preview.users.map((u) => (
                  <tr key={u.email} className="border-t border-border/60">
                    <td className="px-3 py-2">
                      <p className="font-medium">{u.name}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </td>
                    <td className="px-3 py-2">{u.allowance_this_year != null ? `${u.allowance_this_year} ${u.allowance_unit === 'hours' ? 'hours' : 'days'}` : '—'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {(u.working_days || []).length ? `${u.working_days.length} days/wk` : 'company default'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {u.is_admin && <span className="px-1.5 py-0.5 rounded bg-primary/15 text-primary mr-1">admin</span>}
                      {u.archived && <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground">archived</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Leave-type mapping */}
          <h3 className="font-medium text-sm mb-2">Their leave types → yours</h3>
          <div className="space-y-2 mb-5">
            {Object.entries(preview.leave_types).map(([name, s]) => (
              <div key={name} className="flex items-center gap-3">
                <span className="text-sm w-56 truncate" title={name}>
                  {name} <span className="text-xs text-muted-foreground">({s.booking_count})</span>
                </span>
                <span className="text-muted-foreground">→</span>
                <select
                  value={typeMap[name] || 'create:record'}
                  onChange={(e) => setTypeMap({ ...typeMap, [name]: e.target.value })}
                  className="input-hive flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                >
                  {preview.existing_leave_types.map((t) => (
                    <option key={t.id} value={`id:${t.id}`}>
                      Existing: {t.name}{t.deducted ? '' : ' (record only)'}
                    </option>
                  ))}
                  <option value="create:deducted">Create new — deducts from allowance</option>
                  <option value="create:record">Create new — record only</option>
                </select>
              </div>
            ))}
          </div>

          {/* Warnings */}
          {(preview.warnings || []).length > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 mb-5 space-y-1">
              <div className="flex items-center gap-2 text-amber-600 text-sm font-medium">
                <AlertTriangle className="w-4 h-4" /> Worth knowing
              </div>
              {preview.warnings.map((w, i) => (
                <p key={i} className="text-xs text-muted-foreground">{w}</p>
              ))}
            </div>
          )}

          {/* Column report — the drift detector */}
          <details className="mb-5">
            <summary className="text-xs text-muted-foreground cursor-pointer">
              What we read from the file (column report)
            </summary>
            <div className="mt-2 space-y-2">
              {Object.entries(preview.column_report).map(([sheet, rep]) => (
                <p key={sheet} className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{sheet}:</span>{' '}
                  {rep.recognised.length} column(s) used
                  {rep.ignored.length ? ` · ignored: ${rep.ignored.join(', ')}` : ''}
                  {rep.missing.length ? ` · MISSING: ${rep.missing.join(', ')}` : ''}
                </p>
              ))}
            </div>
          </details>

          {/* "That's me" — offered when the admin's signup email isn't in the
              file. Without it, the import creates a SECOND them, with their
              history attached to the twin (Rob, 16 Jul). */}
          {preview.admin_in_file ? (
            <p className="text-xs text-muted-foreground mb-5">
              ✓ You're in this file as <span className="font-medium text-foreground">{preview.admin_email}</span> — your
              account and history will be merged automatically.
            </p>
          ) : (
            <div className="mb-5">
              <Label className="text-sm">Are you one of these people?</Label>
              <p className="text-xs text-muted-foreground mb-1.5">
                You signed up as <span className="font-medium text-foreground">{preview.admin_email}</span>, which
                isn't in this file. If you're listed under a different address,
                pick yourself so your history lands on YOUR account instead of
                creating a duplicate person.
              </p>
              <select
                value={selfEmail}
                onChange={(e) => setSelfEmail(e.target.value)}
                className="input-hive w-full max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="tt-import-self"
              >
                <option value="">I'm not in this file</option>
                {preview.users.map((u) => (
                  <option key={u.email} value={u.email}>
                    {u.name} — {u.email}
                  </option>
                ))}
              </select>
            </div>
          )}

          <label className="flex items-center gap-3 cursor-pointer mb-5">
            <input type="checkbox" checked={sendInvites}
                   onChange={(e) => setSendInvites(e.target.checked)}
                   className="w-4 h-4 rounded border-input accent-primary" />
            <span className="text-sm">
              Send login invites to imported employees straight away
              <span className="block text-xs text-muted-foreground">
                Off by default — you can check everything landed correctly first,
                then invite people from the Employees page.
              </span>
            </span>
          </label>

          <div className="flex gap-3">
            <Button variant="outline" onClick={reset} disabled={busy}>Start over</Button>
            <Button className="btn-primary" onClick={doImport} disabled={busy} data-testid="tt-import-apply">
              {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing…</>) : `Import ${preview.counts.users} employees & ${preview.counts.bookings} bookings`}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ----- step 1: upload -----------------------------------------------------
  return (
    <div className="card-hive p-6 max-w-2xl">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center">
          <Upload className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold">Switch from Timetastic</h2>
          <p className="text-sm text-muted-foreground">
            Bring your employees, allowances and full booking history across in minutes.
          </p>
        </div>
      </div>
      <ol className="text-sm text-muted-foreground list-decimal ml-5 my-4 space-y-1">
        <li>In Timetastic go to <span className="font-medium text-foreground">Settings → Reporting &amp; backup</span></li>
        <li>Under <span className="font-medium text-foreground">Full backup</span>, choose Excel and download</li>
        <li>Upload that file here — you'll see a full preview before anything is imported</li>
      </ol>
      <label className="flex items-center gap-3 border border-dashed border-border rounded-lg p-4 cursor-pointer hover:bg-secondary/40 transition-colors">
        <FileSpreadsheet className="w-6 h-6 text-muted-foreground" />
        <div className="flex-1">
          <p className="text-sm font-medium">{file ? file.name : 'Choose the backup file'}</p>
          <p className="text-xs text-muted-foreground">Excel (.xlsx) — the Full backup export</p>
        </div>
        <input type="file" accept=".xlsx" className="hidden" data-testid="tt-import-file"
               onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </label>
      <div className="mt-4">
        <Button className="btn-primary" onClick={doPreview} disabled={!file || busy} data-testid="tt-import-preview">
          {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reading…</>) : 'Preview import'}
        </Button>
      </div>
    </div>
  );
}
